import { Combobox as HeadlessCombobox } from '@headlessui/react';
import * as Popper from '@radix-ui/react-popper';
import classNames from 'classnames';
import { isEmpty, isNull } from 'lodash-es';
import { CaretDown, CaretUp, Check } from 'phosphor-react';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo } from 'react';
import type { FormFieldVariant } from '../form-field-variant';
import type { Option } from '../option';
import { toOption } from '../option';
import { useOptionContainerViewportAwareResize } from '../option-container-viewport-aware-resize';

type ComboboxProps<Value> = {
  options: (Option<Value> | Value)[];
  toDisplayValue: (value: Value | undefined) => string;
  toKey: (value: Value) => string;
  variant?: FormFieldVariant;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  value: Value | undefined;
  onValueChange: (newValue: Value | undefined) => void;
  onQueryChange: (query: string) => void;
};

export function Combobox<Value>({
  options: availableOptions,
  toKey,
  toDisplayValue: toDisplayValueFn,
  variant = 'default',
  className,
  disabled = false,
  placeholder,
  value,
  onValueChange,
  onQueryChange,
}: ComboboxProps<Value>) {
  const { triggerRef, containerRef } = useOptionContainerViewportAwareResize<HTMLDivElement, HTMLDivElement>();
  const options = useMemo(() => availableOptions.map(toOption), [availableOptions]);
  const toDisplayValue = useMemo(() => nullToUndefined(toDisplayValueFn), [toDisplayValueFn]);

  const handleValueChange = useCallback((value: Value | null) => {
    onValueChange(value ?? undefined);
  }, [onValueChange]);

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    if (isEmpty(query)) {
      handleValueChange(null);
    }
    onQueryChange(query);
  }, [handleValueChange, onQueryChange]);

  return (
    <Popper.Root>
      <HeadlessCombobox value={value ?? null} onChange={handleValueChange} nullable={true} disabled={disabled}>
        <Popper.Anchor
          className="inline-flex"
          ref={triggerRef}>
          <HeadlessCombobox.Input
            className={classNames('tds-input-field', className, {
              'tds-combobox-default': variant === 'default',
              'tds-combobox-small': variant === 'small',
              'bg-contrast text-dark': !disabled,
              'cursor-default': disabled,
            })}
            placeholder={placeholder}
            displayValue={toDisplayValue}
            onChange={handleQueryChange}
          />
          <HeadlessCombobox.Button>
            {({ open }) => open ? (
              <CaretUp size={16} className={classNames('tds-input-icon', { disabled })} />
            ) : (
              <CaretDown size={16} className={classNames('tds-input-icon', { disabled })} />
            )}
          </HeadlessCombobox.Button>
        </Popper.Anchor>

        <Popper.Content ref={containerRef}>
          <HeadlessCombobox.Options
            as="ul"
            className={classNames('mt-2 select-none tds-option-container tds-input-value', {
              'tds-input-value-default': variant === 'default',
              'tds-input-value-small': variant === 'small',
            })}
          >
            {options.map(({ value, disabled }) => (
              <HeadlessCombobox.Option
                as="li"
                className="flex items-center justify-between tds-option-container-item aria-checked:pr-3"
                key={toKey(value)}
                value={value}
                disabled={disabled}
              >
                {({ selected }) => (<>
                  {toDisplayValue(value)}
                  {selected ? <Check size={14} className="tds-input-icon"/> : null}
                </>)}
              </HeadlessCombobox.Option>
            ))}
          </HeadlessCombobox.Options>
        </Popper.Content>
      </HeadlessCombobox>
    </Popper.Root>
  );
}

function nullToUndefined<T, R>(fn: (arg: T | undefined) => R): (arg: T | undefined | null) => R {
  return (arg: T | undefined | null) => fn(isNull(arg) ? undefined : arg);
}
